import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import CashierLayout from '../components/CashierLayout';
import DenominationGrid from '../components/DenominationGrid';
import { cashierCloseShiftBlind, cashierGetShift } from '../services/cashierApi';
import { emptyDenominationGrid, formatCurrency } from '../utils/denominations';
import { canJoinShift } from '../utils/shiftAccess';

type CloseResult = {
  shiftId: string;
  actualClosingCash: number;
  expectedClosingCash: number;
  discrepancyAmount: number;
  discrepancyFlagged: boolean;
  status: string;
};

export default function ClosingCountPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [denominations, setDenominations] = useState(emptyDenominationGrid());
  const [result, setResult] = useState<CloseResult | null>(null);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await cashierGetShift(id);
        if (!res.success || !res.data?.shift) {
          toast.error(res.message || 'Shift not found');
          navigate('/', { replace: true });
          return;
        }
        if (!canJoinShift(res.data.shift.status)) {
          navigate(`/shifts/${id}`, { replace: true });
        }
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [id, navigate]);

  if (!id) return <Navigate to="/" replace />;

  const onSubmit = async () => {
    const items = denominations.filter((d) => d.quantity > 0);
    if (items.length === 0) {
      toast.error('Enter at least one denomination');
      return;
    }
    setSubmitting(true);
    try {
      const res = await cashierCloseShiftBlind(id, { denominations: items });
      if (!res.success || !res.data) {
        toast.error(res.message || res.code || 'Close shift failed');
        return;
      }
      setResult(res.data);
      toast.success(res.data.discrepancyFlagged ? 'Shift closed — flagged for review' : 'Shift closed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CashierLayout title="Closing count" subtitle="Blind close — count drawer without expected amount" backTo={`/shifts/${id}`}>
      {loading ? (
        <div className="text-sm text-stone-600">Loading…</div>
      ) : result ? (
        <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-6 space-y-4">
          <div className="text-sm font-semibold text-stone-900">Shift closed</div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg bg-stone-50 p-3">
              <div className="text-xs text-stone-500">Your count</div>
              <div className="text-lg font-bold text-stone-900">{formatCurrency(result.actualClosingCash)}</div>
            </div>
            <div className="rounded-lg bg-stone-50 p-3">
              <div className="text-xs text-stone-500">Expected</div>
              <div className="text-lg font-bold text-stone-900">{formatCurrency(result.expectedClosingCash)}</div>
            </div>
            <div className="rounded-lg bg-stone-50 p-3">
              <div className="text-xs text-stone-500">Discrepancy</div>
              <div className={`text-lg font-bold ${result.discrepancyFlagged ? 'text-rose-700' : 'text-emerald-700'}`}>
                {formatCurrency(result.discrepancyAmount)}
              </div>
            </div>
            <div className="rounded-lg bg-stone-50 p-3">
              <div className="text-xs text-stone-500">Status</div>
              <div className="text-sm font-semibold text-stone-900">{result.status.replace(/_/g, ' ')}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/', { replace: true })}
            className="w-full rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            Back to my shifts
          </button>
        </div>
      ) : (
        <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-6">
          <p className="text-sm text-stone-600">
            Count all cash in the drawer. The expected amount is not shown until after you submit.
          </p>
          <div className="mt-4">
            <DenominationGrid value={denominations} onChange={setDenominations} disabled={submitting} />
          </div>
          <button
            type="button"
            onClick={() => void onSubmit()}
            disabled={submitting}
            className="mt-6 w-full rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
          >
            {submitting ? 'Closing shift…' : 'Submit closing count'}
          </button>
        </div>
      )}
    </CashierLayout>
  );
}
